import api from '../../utils/api';
import type { UserAnalyticsSession, UserAnalyticsTimelineEvent } from './userService';
import type { SessionRangeFilter } from './userService';
import {
  VendorResponse,
  VendorDisplay,
  VendorLoginSnapshot,
  VendorExportField,
  VendorExportFieldKey,
} from '../../models/MasterAdmin/vendor';
import { formatDoctorName } from '../../utils/doctorName';

// Vendor role mapping
export const vendorRoleMapping: Record<string, string> = {
  "1": "Hospital",
  "2": "Clinic",
  "3": "Medical Store",
  "4": "Ambulance Agency",
  "5": "Blood Bank",
  "6": "Diagnostic Center",
  "7": "Delivery Partner",
  "8": "Product Partner"
};

// Interface for vendor count response
export interface VendorCountResponse {
  [key: string]: {
    total: number;
    active: number;
  };
}

export interface VendorCategorySummary {
  role: number;
  name: string;
  total: number;
  active: number;
}

export interface VendorPerformancePeriodData {
  name: string;
  visits: number;
  bookings: number;
  revenue: number;
}

export interface VendorPerformanceOverview {
  daily: VendorPerformancePeriodData[];
  weekly: VendorPerformancePeriodData[];
  monthly: VendorPerformancePeriodData[];
  quarterly: VendorPerformancePeriodData[];
}

export interface DemographicMetric {
  visits: number;
  bookings: number;
  revenue: number;
}

export interface VendorDemographics {
  ageGroups: Record<string, DemographicMetric>;
  gender: Record<string, DemographicMetric>;
  healthCondition: Record<string, DemographicMetric>;
}

export interface TopPerformingVendor {
  id: string;
  name: string;
  category: string;
  location: string;
  bookings: number;
  revenue: number;
}

export interface VendorDashboardSummaryResponse {
  success: boolean;
  data: {
    totalRegisteredVendors: number;
    totalActiveVendors: number;
    totalBookings: number;
    vendorCategories: VendorCategorySummary[];
    authSessions: {
      total: number;
      active: number;
      byPlatform: {
        android: number;
        ios: number;
        web: number;
        unknown: number;
      };
    };
    fcmTokens: {
      total: number;
      active: number;
      byPlatform: {
        android: number;
        ios: number;
        web: number;
        unknown: number;
      };
    };
    performanceOverview: VendorPerformanceOverview;
    demographics: VendorDemographics;
    topPerformingVendors: TopPerformingVendor[];
  };
}

export interface VendorCommissionRateResponse {
  success: boolean;
  data: {
    vendorId: string;
    vendorType: 'ambulance' | 'medical_store' | string;
    commissionRate: number;
    referralCommission: number;
  };
  message?: string;
}

export interface VendorCommissionDefault {
  VendorCommissionDefaultId: string;
  vendorType: number;
  commissionType: 'PERCENTAGE';
  commissionValue: number | string;
  isCommissionActive: boolean;
  taxPercent: number | string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface VendorCommissionDefaultsResponse {
  success: boolean;
  data: VendorCommissionDefault[];
  message?: string;
}

// Fetch vendor counts by role
export const fetchVendorCounts = async (): Promise<VendorCountResponse> => {
  try {
    const response = await api.get('/master-admin/vendors/count-by-role');
    return response.data;
  } catch (error) {
    console.error('Error fetching vendor counts:', error);
    throw error;
  }
};

export const fetchVendorDashboardSummary = async (serviceType?: string): Promise<VendorDashboardSummaryResponse["data"]> => {
  try {
    const url = serviceType && serviceType !== 'ALL'
      ? `/master-admin/dashboard/vendor-summary?serviceType=${serviceType}`
      : '/master-admin/dashboard/vendor-summary';
    const response = await api.get<VendorDashboardSummaryResponse>(url);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching vendor dashboard summary:', error);
    throw error;
  }
};

// Fetch all vendors
export const fetchVendors = async (): Promise<VendorResponse> => {
  try {
    const response = await api.get('/master-admin/vendors');
    return response.data;
  } catch (error) {
    console.error('Error fetching vendors:', error);
    throw error;
  }
};

// Delete a vendor
export const deleteVendor = async (vendorId: string): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.delete(`/master-admin/delete-vendor/${vendorId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting vendor:', error);
    throw error;
  }
};

// Toggle vendor status
export const toggleVendorStatus = async (vendorId: string): Promise<{ success: boolean; data: { status: string } }> => {
  try {
    const response = await api.patch(`/master-admin/vendors/${vendorId}/toggle-status`);
    return response.data;
  } catch (error) {
    console.error('Error toggling vendor status:', error);
    throw error;
  }
};

export const fetchVendorCommissionRate = async (vendorId: string): Promise<VendorCommissionRateResponse> => {
  try {
    const response = await api.get(`/master-admin/vendors/${vendorId}/commission-rate`);
    return response.data;
  } catch (error) {
    console.error('Error fetching vendor commission rate:', error);
    throw error;
  }
};

export const updateVendorCommissionRate = async (
  vendorId: string,
  payload: { commissionRate: number; referralCommission: number }
): Promise<VendorCommissionRateResponse> => {
  try {
    const response = await api.patch(`/master-admin/vendors/${vendorId}/commission-rate`, payload);
    return response.data;
  } catch (error) {
    console.error('Error updating vendor commission rate:', error);
    throw error;
  }
};

export const fetchVendorCommissionDefaults = async (): Promise<VendorCommissionDefaultsResponse> => {
  try {
    const response = await api.get('/master-admin/commission-defaults');
    return response.data;
  } catch (error) {
    console.error('Error fetching default vendor commissions:', error);
    throw error;
  }
};

export const createVendorCommissionDefault = async (payload: {
  vendorType: number;
  commissionRate: number;
  isCommissionActive?: boolean;
  taxPercent?: number;
}): Promise<{ success: boolean; data: VendorCommissionDefault; message?: string }> => {
  try {
    const response = await api.post('/master-admin/commission-defaults', payload);
    return response.data;
  } catch (error) {
    console.error('Error creating default vendor commission:', error);
    throw error;
  }
};

export const updateVendorCommissionDefault = async (
  vendorType: number,
  payload: {
    commissionRate?: number;
    isCommissionActive?: boolean;
    taxPercent?: number;
    isActive?: boolean;
  }
): Promise<{ success: boolean; data: VendorCommissionDefault; message?: string }> => {
  try {
    const response = await api.patch(`/master-admin/commission-defaults/${vendorType}`, payload);
    return response.data;
  } catch (error) {
    console.error('Error updating default vendor commission:', error);
    throw error;
  }
};

/** Vendor app login sessions (vendor_auth_sessions), same shape as user analytics auth activity rows */
export interface VendorAuthActivitySession {
  sessionId: string;
  deviceId: string | null;
  platform: string | null;
  appVersion?: string | null;
  appBuildNumber?: string | null;
  osVersion?: string | null;
  deviceModel?: string | null;
  loginMethod?: string | null;
  userAgent?: string | null;
  status: string;
  loginAt: string;
  logoutAt: string | null;
  logoutReason: string | null;
}

export interface VendorAuthActivity {
  isLoggedIn: boolean;
  activeSessions: number;
  totalLoginSessions: number;
  lastLogin: string | null;
  lastLogout: string | null;
  recentSessions: VendorAuthActivitySession[];
}

export const fetchVendorAuthActivity = async (
  vendorId: string
): Promise<{ success: boolean; data: { authActivity: VendorAuthActivity } }> => {
  const response = await api.get(`/master-admin/vendors/${vendorId}/auth-activity`);
  return response.data;
};

/** BigQuery vendor-app events (e.g. VENDOR_AMBULANCE_*), grouped like user analytics */
export interface VendorEventAnalyticsPayload {
  timeline: UserAnalyticsTimelineEvent[];
  sessions: UserAnalyticsSession[];
  dateRange?: { range: string; from: string; to: string };
}

export const fetchVendorEventAnalytics = async (
  vendorId: string,
  params?: { range?: SessionRangeFilter; from?: string; to?: string }
): Promise<{ success: boolean; data: VendorEventAnalyticsPayload }> => {
  const qs = new URLSearchParams();
  if (params?.range) qs.set('range', params.range);
  if (params?.from) qs.set('from', params.from);
  if (params?.to) qs.set('to', params.to);
  const q = qs.toString() ? `?${qs.toString()}` : '';
  const response = await api.get(`/master-admin/vendors/${vendorId}/event-analytics${q}`);
  return response.data;
};

// Process vendors data into a unified format for display
export const processVendorsForDisplay = (response: VendorResponse): VendorDisplay[] => {
  const result: VendorDisplay[] = [];
  const loginSnapshots = response.data?.loginSnapshots ?? {};
  const loginSnap = (vendorId: string): VendorLoginSnapshot | null => loginSnapshots[vendorId] ?? null;
  const clinicsWithTimeslots = new Set(response.data?.clinicsWithTimeslots ?? []);

  // Process medical stores
  if (response.data.medicals) {
    console.log('Processing medical stores:', response.data.medicals.map(v => ({
      vendorId: v.vendorId,
      status: v.Vendor?.status,
      isActive: v.Vendor?.isActive,
      rawVendor: v.Vendor
    })));
    response.data.medicals.forEach(vendor => {
      const isActive = vendor.Vendor?.isActive === false ? false : true;
      console.log(`Medical store ${vendor.vendorId} isActive:`, {
        rawValue: vendor.Vendor?.isActive,
        processedValue: isActive,
        vendor: vendor.Vendor
      });
      result.push({
        id: vendor.vendorId,
        name: vendor.name || 'Unnamed Store',
        category: 'Medical Store',
        email: vendor.emailId || vendor.Vendor?.email || '',
        phone: vendor.contactNumber || vendor.Vendor?.phoneNumber || '',
        address: `${vendor.address || ''}, ${vendor.city || ''}, ${vendor.state || ''}`.trim(),
        status: vendor.Vendor?.status || 'pending',
        isActive,
        createdAt: vendor.Vendor?.createdAt || new Date().toISOString(),
        profilePicture: vendor.profilePicture,
        originalData: vendor,
        loginSnapshot: loginSnap(vendor.vendorId),
      });
    });
  }

  // Process ambulances
  if (response.data.ambulances) {
    console.log('Processing ambulances:', response.data.ambulances.map(v => ({
      vendorId: v.vendorId,
      status: v.Vendor?.status,
      isActive: v.Vendor?.isActive,
      rawVendor: v.Vendor
    })));
    response.data.ambulances.forEach(vendor => {
      const isActive = vendor.Vendor?.isActive === false ? false : true;
      console.log(`Ambulance ${vendor.vendorId} isActive:`, {
        rawValue: vendor.Vendor?.isActive,
        processedValue: isActive,
        vendor: vendor.Vendor
      });
      result.push({
        id: vendor.vendorId,
        name: vendor.agencyName || 'Unnamed Agency',
        category: 'Ambulance',
        email: vendor.email || vendor.Vendor?.email || '',
        phone: vendor.contactNumber || vendor.Vendor?.phoneNumber || '',
        address: `${vendor.address || ''}, ${vendor.city || ''}, ${vendor.state || ''}`.trim(),
        status: vendor.Vendor?.status || 'pending',
        isActive,
        createdAt: vendor.Vendor?.createdAt || new Date().toISOString(),
        profilePicture: vendor.profilePicture,
        originalData: vendor,
        loginSnapshot: loginSnap(vendor.vendorId),
      });
    });
  }

  // Process blood banks
  if (response.data.bloodBanks) {
    console.log('Processing blood banks:', response.data.bloodBanks.map(v => ({
      vendorId: v.vendorId,
      status: v.Vendor?.status,
      isActive: v.Vendor?.isActive,
      rawVendor: v.Vendor
    })));
    response.data.bloodBanks.forEach(vendor => {
      const isActive = vendor.Vendor?.isActive === false ? false : true;
      console.log(`Blood bank ${vendor.vendorId} isActive:`, {
        rawValue: vendor.Vendor?.isActive,
        processedValue: isActive,
        vendor: vendor.Vendor
      });
      result.push({
        id: vendor.vendorId,
        name: vendor.agencyName || 'Unnamed Blood Bank',
        category: 'Blood Bank',
        email: vendor.email || vendor.Vendor?.email || '',
        phone: vendor.phoneNumber || vendor.Vendor?.phoneNumber || '',
        address: `${vendor.completeAddress || ''}, ${vendor.city || ''}, ${vendor.state || ''}`.trim(),
        status: vendor.Vendor?.status || 'pending',
        isActive,
        createdAt: vendor.Vendor?.createdAt || new Date().toISOString(),
        profilePicture: vendor.profilePicture,
        originalData: vendor,
        loginSnapshot: loginSnap(vendor.vendorId),
      });
    });
  }

  // Process clinics
  if (response.data.clinics) {
    console.log('Processing clinics:', response.data.clinics.map(v => ({
      vendorId: v.vendorId,
      status: v.vendor?.status,
      isActive: v.vendor?.isActive,
      rawVendor: v.vendor
    })));
    response.data.clinics.forEach(vendor => {
      const isActive = vendor.vendor?.isActive === false ? false : true;
      console.log(`Clinic ${vendor.vendorId} isActive:`, {
        rawValue: vendor.vendor?.isActive,
        processedValue: isActive,
        vendor: vendor.vendor
      });
      result.push({
        id: vendor.vendorId,
        name: vendor.doctorName || 'Unnamed Clinic',
        category: 'Doctor Clinic',
        email: vendor.email || vendor.vendor?.email || '',
        phone: vendor.phoneNumber || vendor.vendor?.phoneNumber || '',
        address: `${vendor.address || ''}, ${vendor.city || ''}, ${vendor.state || ''}`.trim(),
        status: vendor.vendor?.status || 'pending',
        isActive,
        createdAt: vendor.vendor?.createdAt || new Date().toISOString(),
        profilePicture: vendor.profilePicture,
        originalData: vendor,
        loginSnapshot: loginSnap(vendor.vendorId),
        hasTimeSlots: clinicsWithTimeslots.has(vendor.vendorId),
      });
    });
  }

  // Process hospitals
  if (response.data.hospitals) {
    console.log('Processing hospitals:', response.data.hospitals.map(v => ({
      vendorId: v.vendorId,
      status: v.vendor?.status,
      isActive: v.vendor?.isActive,
      rawVendor: v.vendor
    })));
    response.data.hospitals.forEach(vendor => {
      const isActive = vendor.vendor?.isActive === false ? false : true;
      console.log(`Hospital ${vendor.vendorId} isActive:`, {
        rawValue: vendor.vendor?.isActive,
        processedValue: isActive,
        vendor: vendor.vendor
      });
      result.push({
        id: vendor.vendorId,
        name: vendor.name || 'Unnamed Hospital',
        category: 'Hospital',
        email: vendor.email || vendor.vendor?.email || '',
        phone: vendor.contactNumber || vendor.vendor?.phoneNumber || '',
        address: `${vendor.address || ''}, ${vendor.city || ''}, ${vendor.state || ''}`.trim(),
        status: vendor.vendor?.status || 'pending',
        isActive,
        createdAt: vendor.vendor?.createdAt || new Date().toISOString(),
        profilePicture: vendor.photos?.[0]?.url,
        originalData: vendor,
        loginSnapshot: loginSnap(vendor.vendorId),
      });
    });
  }

  // Process diagnostic centers
  if (response.data.diagnostics) {
    console.log('Processing diagnostic centers:', response.data.diagnostics.map(v => ({
      vendorId: v.vendorId,
      status: v.vendor?.status,
      isActive: v.vendor?.isActive,
      rawVendor: v.vendor
    })));
    response.data.diagnostics.forEach(vendor => {
      const isActive = vendor.vendor?.isActive === false ? false : true;
      console.log(`Diagnostic center ${vendor.vendorId} isActive:`, {
        rawValue: vendor.vendor?.isActive,
        processedValue: isActive,
        vendor: vendor.vendor
      });
      result.push({
        id: vendor.vendorId,
        name: vendor.name || 'Unnamed Diagnostic Center',
        category: 'Diagnostic Center',
        email: vendor.email || vendor.vendor?.email || '',
        phone: vendor.mainContactNumber || vendor.vendor?.phoneNumber || '',
        address: `${vendor.address || ''}, ${vendor.city || ''}, ${vendor.state || ''}`.trim(),
        status: vendor.vendor?.status || 'pending',
        isActive,
        createdAt: vendor.vendor?.createdAt || new Date().toISOString(),
        profilePicture: vendor.centerPhotosUrl,
        originalData: vendor,
        loginSnapshot: loginSnap(vendor.vendorId),
      });
    });
  }

  // Process product partners
  if (response.data.productPartners) {
    console.log('Processing product partners:', response.data.productPartners.map(v => ({
      vendorId: v.vendorId,
      status: v.vendor?.status,
      isActive: v.vendor?.isActive,
      rawVendor: v.vendor
    })));
    response.data.productPartners.forEach(vendor => {
      const isActive = vendor.vendor?.isActive === false ? false : true;
      console.log(`Product partner ${vendor.vendorId} isActive:`, {
        rawValue: vendor.vendor?.isActive,
        processedValue: isActive,
        vendor: vendor.vendor
      });
      result.push({
        id: vendor.vendorId,
        name: vendor.companyLegalName || vendor.brandName || 'Unnamed Product Partner',
        category: 'Product Partner',
        email: vendor.email || vendor.vendor?.email || '',
        phone: vendor.phoneNumber || vendor.vendor?.phoneNumber || '',
        address: `${vendor.address || ''}, ${vendor.city || ''}, ${vendor.state || ''}`.trim(),
        status: vendor.vendor?.status || 'pending',
        isActive,
        createdAt: vendor.vendor?.createdAt || new Date().toISOString(),
        profilePicture: vendor.profilePicture,
        originalData: vendor,
        loginSnapshot: loginSnap(vendor.vendorId),
      });
    });
  }

  console.log('Final processed vendors:', result.map(v => ({
    id: v.id,
    name: v.name,
    status: v.status,
    isActive: v.isActive,
    originalData: v.originalData.Vendor || v.originalData.vendor
  })));
  return result;
};

export const VENDOR_EXPORT_FIELDS: VendorExportField[] = [
  { key: 'name', label: 'Vendor Name', defaultChecked: true },
  { key: 'category', label: 'Category', defaultChecked: true },
  { key: 'email', label: 'Email', defaultChecked: true },
  { key: 'phone', label: 'Phone', defaultChecked: true },
  { key: 'address', label: 'Address', defaultChecked: true },
  { key: 'city', label: 'City', defaultChecked: true },
  { key: 'state', label: 'State', defaultChecked: true },
  { key: 'status', label: 'Account Status', defaultChecked: true },
  { key: 'isActive', label: 'Active Status', defaultChecked: true },
  { key: 'loginStatus', label: 'Login Status', defaultChecked: true },
  { key: 'platform', label: 'Platform', defaultChecked: true },
  { key: 'vendorId', label: 'Vendor ID', defaultChecked: true },
  { key: 'generatedId', label: 'Generated ID', defaultChecked: true },
  { key: 'createdAt', label: 'Onboard Date & Time', defaultChecked: true },
  { key: 'hasTimeSlots', label: 'Time Slots Added', defaultChecked: true },
];

const formatExportDate = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const formatExportPlatforms = (platforms: string[] | undefined) => {
  if (!platforms?.length) return '';
  return platforms
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join(', ');
};

const formatExportLoginStatus = (snapshot: VendorLoginSnapshot | null | undefined) => {
  if (!snapshot) return '';
  switch (snapshot.loginStatus) {
    case 'LOGGED_IN':
      return 'Logged in';
    case 'LOGGED_OUT':
      return 'Logged out';
    case 'EXPIRED':
      return 'Expired';
    case 'REVOKED':
      return 'Revoked';
    case 'ACTIVE':
      return 'Active session';
    case 'NONE':
      return 'No login data';
    default:
      return snapshot.loginStatus.replace(/_/g, ' ');
  }
};

const getVendorExportDisplayName = (vendor: VendorDisplay) =>
  vendor.category === 'Doctor Clinic' ? formatDoctorName(vendor.name) : vendor.name;

const getVendorBaseRecord = (vendor: VendorDisplay) =>
  vendor.originalData?.Vendor || vendor.originalData?.vendor || {};

export const getVendorExportFieldValue = (
  vendor: VendorDisplay,
  field: VendorExportFieldKey
): string => {
  const base = getVendorBaseRecord(vendor);
  switch (field) {
    case 'name':
      return getVendorExportDisplayName(vendor);
    case 'category':
      return vendor.category;
    case 'email':
      return vendor.email || '';
    case 'phone':
      return vendor.phone || '';
    case 'address':
      return vendor.address || vendor.originalData?.address || vendor.originalData?.completeAddress || '';
    case 'city':
      return vendor.originalData?.city || '';
    case 'state':
      return vendor.originalData?.state || '';
    case 'status':
      return vendor.status.charAt(0).toUpperCase() + vendor.status.slice(1);
    case 'isActive':
      return vendor.isActive ? 'Active' : 'Inactive';
    case 'loginStatus':
      return formatExportLoginStatus(vendor.loginSnapshot);
    case 'platform':
      return formatExportPlatforms(vendor.loginSnapshot?.platforms);
    case 'vendorId':
      return vendor.id;
    case 'generatedId':
      return base.generatedId || vendor.originalData?.generatedId || '';
    case 'createdAt':
      return formatExportDate(vendor.createdAt || base.createdAt);
    case 'hasTimeSlots':
      return vendor.category === 'Doctor Clinic' ? (vendor.hasTimeSlots ? 'Yes' : 'No') : '—';
    default:
      return '';
  }
};

const escapeExcelCell = (value: string | number | boolean | null | undefined) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

export const exportVendorsToExcel = (
  vendors: VendorDisplay[],
  selectedFields: VendorExportFieldKey[]
) => {
  const fields = VENDOR_EXPORT_FIELDS.filter((field) => selectedFields.includes(field.key));
  if (!fields.length || !vendors.length) return;

  const columns = fields.map((field) => field.label);
  const rows = vendors.map((vendor) =>
    fields.map((field) => getVendorExportFieldValue(vendor, field.key))
  );

  const tableHead = columns.map((column) => `<th>${escapeExcelCell(column)}</th>`).join('');
  const tableBody = rows
    .map(
      (row) =>
        `<tr>${row.map((cell) => `<td>${escapeExcelCell(cell)}</td>`).join('')}</tr>`
    )
    .join('');

  const worksheet = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="UTF-8" />
        <!--[if gte mso 9]>
        <xml>
          <x:ExcelWorkbook>
            <x:ExcelWorksheets>
              <x:ExcelWorksheet>
                <x:Name>Vendors</x:Name>
                <x:WorksheetOptions><x:DisplayGridlines /></x:WorksheetOptions>
              </x:ExcelWorksheet>
            </x:ExcelWorksheets>
          </x:ExcelWorkbook>
        </xml>
        <![endif]-->
      </head>
      <body>
        <table>
          <thead><tr>${tableHead}</tr></thead>
          <tbody>${tableBody}</tbody>
        </table>
      </body>
    </html>
  `;

  const blob = new Blob([worksheet], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  const date = new Date().toISOString().slice(0, 10);

  link.href = url;
  link.download = `vedika-vendors-${date}.xls`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}; 